import { useTranslation } from "react-i18next";
import { MapContainer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import GoogleTileLayer from "./GoogleTileLayer";

const createPointIcon = (color = "#3B82F6") =>
  L.divIcon({
    className: "punch-marker",
    html: `<div style="
      background-color: ${color};
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 0 10px rgba(59, 130, 246, 0.6);
    "></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });

/**
 * Modal que muestra un punto (latitud/longitud) en el mapa.
 *
 * props:
 *  - open / onOpenChange: control del diálogo
 *  - location: { latitude, longitude } | null
 *  - title: texto del encabezado y del popup del marcador
 */
export const LocationMapDialog = ({ open, onOpenChange, location, title }) => {
  const { t } = useTranslation();
  const hasPoint =
    location && Number.isFinite(Number(location.latitude)) && Number.isFinite(Number(location.longitude));
  const lat = hasPoint ? Number(location.latitude) : null;
  const lng = hasPoint ? Number(location.longitude) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {title}
          </DialogTitle>
        </DialogHeader>
        {hasPoint && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-600">{t("pages.punchRecords.coordinates")}</p>
                <p className="font-mono text-xs">
                  {lat.toFixed(6)}, {lng.toFixed(6)}
                </p>
              </div>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank")}
                >
                  {t("pages.punchRecords.openInGoogleMaps")}
                </Button>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden border border-slate-200" style={{ height: "500px" }}>
              <MapContainer
                key={`${lat},${lng}`}
                center={[lat, lng]}
                zoom={18}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
              >
                <GoogleTileLayer />
                <Marker position={[lat, lng]} icon={createPointIcon("#3B82F6")}>
                  <Popup>
                    <div className="p-2">
                      <p className="font-bold text-blue-600">{title}</p>
                      <p className="text-xs font-mono text-slate-500">
                        {lat.toFixed(6)}, {lng.toFixed(6)}
                      </p>
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LocationMapDialog;
